import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  Activum,
  BokkzVpbDataService,
  CorporateTaxEntity,
  Innovatiebox,
  VpbAangifte
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {debounceTime, filter, merge, Observable, shareReplay, take, tap} from "rxjs";
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-innovatiebox',
  templateUrl: './innovatiebox.component.html',
  styleUrls: ['./innovatiebox.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    SharedUiButtonModule,
    CommonModule
  ]
})
export class InnovatieboxComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);

  public dialogVisible = false;
  public bijzonderhedenVisible = false;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Ja'},
    {value: false, label: 'Nee'},
  ];

  public innovatieboxFg = new FormGroup({
    innovaties: new FormArray<FormGroup>([])
  });

  public innovatieboxBijzonderhedenFg = new FormGroup({
    vaststellingsOvereenkomst: new FormControl<boolean | null>(null),
    forfaitaireRegeling: new FormControl<boolean | null>(null),
    saldoInTeLopenVoortbrengingskosten: new FormControl<number | null>(null),
    voordelenBovenDrempel: new FormControl<number | null>(null),
    winstUitVoordelenNietToegewezen: new FormControl<number | null>(null),
  });

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        if (!data.aangifte) return;

      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  get innovaties(): FormArray {
    return this.innovatieboxFg.get('innovaties') as FormArray;
  }

  addRij(): void {
    this.innovaties.push(this.createFormGroup(), { emitEvent: false });
  }

  verwijderRij(index: number): void {
    this.innovaties.removeAt(index, { emitEvent: false });
    this.saveForm();
  }

  showBijzonderheden(): void {
    this.bijzonderhedenVisible = true;
  }

  createFormGroup(item?: Activum): FormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
      voortbrengingskosten: new FormControl<number | null>(item?.voortbrengingskosten ?? null),
    });
  }

  ngOnInit(): void {
    this.corporateTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      const spec = data.aangifte?.specificatieInnovatiebox;
      if (spec) {
        this.innovatieboxBijzonderhedenFg.patchValue(data.aangifte?.specificatieInnovatiebox || {}, {emitEvent: false});
      }
      const lijst: Activum[] = spec?.activumInInnovatiebox ?? [];
      const fa = this.innovaties;
      while (fa.length > lijst.length) fa.removeAt(fa.length - 1);
      for (let i = 0; i < lijst.length; i++) {
        if (i < fa.length) {
          fa.at(i).patchValue(lijst[i], {emitEvent: false});
        } else {
          fa.push(this.createFormGroup(lijst[i]), {emitEvent: false});
        }
      }
    })
    merge(
      this.innovatieboxFg.valueChanges,
      this.innovatieboxBijzonderhedenFg.valueChanges
    ).pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
    });
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  saveForm(): void {
    this.innovatieboxFg.updateValueAndValidity({emitEvent: false});
    this.innovatieboxBijzonderhedenFg.updateValueAndValidity({emitEvent: false});

    const bijz = this.innovatieboxBijzonderhedenFg.getRawValue();
    const activumInInnovatiebox: Activum[] = this.innovaties.getRawValue()
      .filter((row: any) => row.omschrijving || row.voortbrengingskosten != null)
      .map((row: any): Activum => ({
        omschrijving: row.omschrijving ?? undefined,
        voortbrengingskosten: row.voortbrengingskosten ?? undefined,
      }));

    const specificatieInnovatiebox = {
      activumInInnovatiebox,
      vaststellingsOvereenkomst: bijz.vaststellingsOvereenkomst ?? undefined,
      forfaitaireRegeling: bijz.forfaitaireRegeling ?? undefined,
      saldoInTeLopenVoortbrengingskosten: bijz.saldoInTeLopenVoortbrengingskosten ?? undefined,
      voordelenBovenDrempel: bijz.voordelenBovenDrempel ?? undefined,
      winstUitVoordelenNietToegewezen: bijz.winstUitVoordelenNietToegewezen ?? undefined,
    } as Innovatiebox;

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) return currentEntity;
      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          specificatieInnovatiebox,
        } as VpbAangifte,
      };
    });
  }
}
